/**
	* Draw tool view
	* Handles: drawing of features
	* @module DrawTool
*/

/// Imports

// React
import React, {useState, useEffect, Fragment} from 'react';

// OpenLayers
import OlMap from 'ol/Map';
import GeoJSON from 'ol/format/GeoJSON';
import { GeoJSONFeature } from 'ol/format/GeoJSON';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import VectorTile from 'ol/VectorTile';
import VectorTileLayer from 'ol/layer/VectorTile';
import VectorTileSource from 'ol/source/VectorTile';
import Draw from 'ol/interaction/Draw';
import Snap from 'ol/interaction/Snap';
import Feature from 'ol/Feature';
import Geometry from 'ol/geom/Geometry';
import Polygon from 'ol/geom/Polygon';
import LinearRing from 'ol/geom/LinearRing';
import MultiPolygon from 'ol/geom/MultiPolygon';
import MVT from 'ol/format/MVT';
import WKT from 'ol/format/WKT';
import Collection from 'ol/Collection';
import TileState from 'ol/TileState';
import { extend, getCenter, createEmpty as createEmptyExtent } from 'ol/extent';
import { Units } from 'ol/control/ScaleLine';
import { createXYZ } from 'ol/tilegrid';
import { Style, Fill } from 'ol/style';

// Styles
import StyleFeature from 'styles/script/stylefeature';
import StyleFeatureLinearRing from 'styles/script/stylefeaturelinearring';
import StyleSitesFeature from 'styles/script/stylesitesfeature';
import 'styles/css/DrawTool.scss';

// Lib
import DrawToolInteractions from 'lib/interfaces/drawtoolinteractions';
import DrawToolState from 'lib/interfaces/states/drawtool';
import FeatureMode from 'lib/enums/featuremode';
import MapToolMode from 'lib/enums/maptoolmode';
import DrawEvent from 'lib/enums/drawevent';
import ExistingSite from 'lib/classes/existingsite';
import FeatureVariation from 'lib/classes/featurevariation';
import CookieCut from 'lib/classes/cookiecut';
import RemnantPair from 'lib/classes/remnantpair';
import ChangelogMode from 'lib/enums/changelogmode';
import { findDefaultIndex } from 'lib/classes/featurevariation';
import { Popup } from 'lib/classes/popup';

// Vector2
import Vector2 from 'lib/classes/vector2';

// Popups
import FeatureVariationPopup from 'views/popups/FeatureVariation';

// Tools
import { 
	formatFeature, 
	clearSizeInfo, 
	deleteFeature, 
	getNextFeatureId, 
	removeFeatureFromSource, 
	featureInsersects, 
	polygonIntersects,
	polygonIntersections,
	focusFeatures,
	saveFeaturesRemote,
	saveFeaturesLocal,
	onFeatureDrawEvent,
	getFeatures,
	getIsSelfIntersecting,
	getCoordinatesCount,
	validateFeature,
	parseWKTFeature,
	addPolygonToFeature,
	buildSiteInfo
} from 'lib/utilities/feature';
import {
	cookieCut,
	cookieCutReverse
} from 'lib/feature/cookiecutter';
import { 
	drawToolNames,
	getToolEnabled,
	getDrawToolEnabled,
	getDrawToolsEnabled
} from 'lib/utilities/toolmodes';
import { getSnapToleranceMultiplier } from 'lib/utilities/map';
import { fetchJson, fetchRequestJson } from 'lib/utilities/network';
import { assetURL } from 'lib/utilities/asset';

// Lang
import { getLocaleString } from 'lib/localization/vendor';

/// Variable declarations
var map:OlMap;
var mapConfig:any;
var snapFeatures:Collection<Feature<Geometry>> = new Collection<Feature<Geometry>>([], {
	unique: true
});
var saveTimer:any;
const maxZoomSnapping:number = 20;

const keysDown:Map<string, boolean> = new Map<string, boolean>();

/**
	* Draw tool state object
	* 
	* @param initialized		-	Has view been initialized
	* @param featureMode		-	Drawing mode
	* @param activeFeatureVariations  -   Variations of a type of feature
	* 
	* @remarks
	* This is not the same as the React component's state.
	* Values that change during runtime are stored in this state object.
*/
const state:DrawToolState = { 
	initialized: false,
	featureMode: FeatureMode.Polygon,
	activeFeatureVariations: new Map<string, FeatureVariation>()
};

/*
	Vector tile layer for snapping
*/
const vectorTileLayer:VectorTileLayer = new VectorTileLayer({
	source: new VectorTileSource({
		format: new MVT({
			featureClass: Feature,
		}),
		//url: 'https://bluefoxtech.co.uk/demos/vectortiles/{z}/{x}/{y}.pbf',
		url: 'https://vectors.opus4.co.uk/{z}/{x}/{y}.pbf',
		tileGrid: createXYZ({minZoom: 17, maxZoom: 25})
	}),
	minZoom: 17, // slightly outside range to allow for bad rounding
	maxZoom: 25,
	style: []
});

/*
	Drawing source & layer for drawn features
*/
const drawingSource:VectorSource = new VectorSource();
const drawingLayer:VectorLayer<VectorSource> = new VectorLayer({
	source: drawingSource,
	zIndex: 3
});

/*
	Existing sites layer for static features
*/
const sitesSource:VectorSource = new VectorSource();
const sitesLayer:VectorLayer<VectorSource> = new VectorLayer({
	source: sitesSource,
	zIndex: 3
});

/*
	Linear ring source (not rendered)
*/
const linearRingSource:VectorSource = new VectorSource();
const linearRingLayer:VectorLayer<VectorSource> = new VectorLayer({
	source: linearRingSource,
	visible: false
})

// Interactions are set in initialize()
const Interactions:DrawToolInteractions = {}; 

interface DrawtoolProps {
	olMap:OlMap,
	zoomLevel:number,
	currentToolMode:MapToolMode,
}
interface InsertOptions {
	addToSource?:boolean
	serverSync?:boolean,
	selfIntersectionChecking?:boolean,
	isNew?:boolean
}

// Styling overrides
const toolsLeft:number = 124.8;
const toolPadding:number = 43.2;

/* 
	Couldn't get this working (to use instead of the style override variables above)
	The styles object does not contain the needed exported properties for some reason
*/
//import styles from 'styles/css/style.module.scss';

export default function DrawTool({olMap, zoomLevel, currentToolMode}:DrawtoolProps) {
	map = olMap;
	mapConfig = map.get('opus-config');
	
	// State hooks
	const [featureMode, setFeatureMode] = useState(state.featureMode);
	const [drawingPolygon, setDrawingPolygon] = useState<boolean>(false);
	const [cookieCuts, setCookieCuts] = useState<CookieCut[]>([]);
	const [drawToolModesEnabled, setDrawToolModesEnabled] = useState<FeatureMode[]>(getDrawToolsEnabled(mapConfig));
	
	// Effect hook for closing variations popup when feature mode changes
	useEffect(() => {
		if(_ui.isPopupOpen(FeatureVariationPopup))
			showVariationsPanel(featureMode);
		
		if(!getDrawToolEnabled(featureMode, mapConfig)) {
			const drawToolsEnabled:FeatureMode[] = getDrawToolsEnabled(mapConfig);
			
			updateFeatureMode(drawToolsEnabled?.[0]);
		}
			
	}, [featureMode, drawToolModesEnabled]);
	
	useEffect(() => {
		_events.unall('on-cookie-cut');
		_events.on('on-cookie-cut', (result:CookieCut) => {
			setCookieCuts([result, ...cookieCuts]);
		});
	}, [cookieCuts]);
	
	useEffect(() => updateSnap(), [currentToolMode]);
	
	useEffect(() => {
		// When draw tool modes change, update state and redraw
		_events.on('draw-tool-modes-changed', () => {
			setDrawToolModesEnabled(getDrawToolsEnabled(mapConfig));
		});
	}, []);
	
	const updateFeatureMode = (mode:FeatureMode) => {
		setFeatureMode(mode);
		state.featureMode = mode;
		updateInteractionToggle();
	}
	const showVariationsPanel = (mode:FeatureMode) => {
		const modeName:string = FeatureMode[mode];
		const featureVariations:FeatureVariation[] = map.get('feature-variations')?.get(modeName);
		
		if(featureVariations?.length > 0) {
			/* 
				Always set the feature mode even when there's options,
				because the user might not re-select the default variation
			*/
			updateFeatureMode(mode);
			
			FeatureVariationPopup.set('active-variation', state.activeFeatureVariations.get(modeName));
			FeatureVariationPopup.set('variations', featureVariations);
			FeatureVariationPopup.set('mode-name', modeName);
			FeatureVariationPopup.set('onselect', (variation:FeatureVariation) => {
				state.activeFeatureVariations.set(modeName, variation);
				updateFeatureMode(mode);
				_ui.setToolMode(MapToolMode.Draw);
			});
			
			return _ui.showPopup(FeatureVariationPopup);
		}
	}
	const drawFeatureModes = () => {
		const rows:any[] = [];
		
		const featureModes:FeatureMode[] = Array.from(drawToolNames.keys());
		
		featureModes.forEach((mode:FeatureMode) => {
			getDrawToolEnabled(mode, mapConfig) && rows.push(
				drawFeatureModeToolButton(mode, rows.length)
			);
		});
		
		return rows;
	}
	const drawFeatureModeToolButton = (mode:FeatureMode, key:number) => {
		const modeName:string = FeatureMode[mode];
		const className:string = `drawing-option drawing-option--${modeName.toLowerCase()} ${featureMode == mode ? ` drawing-option--selected ${_ui.getSelClassName()}` : ''}`;
		const className2:string = `drawing-option drawing-option--${modeName.toLowerCase()}`;
		
		const featureVariations:FeatureVariation[] = map.get('feature-variations')?.get(modeName);
		const featureVariation:FeatureVariation = state.activeFeatureVariations.get(modeName);
		
		const left:number = toolsLeft + (key * toolPadding);
		
		const onClick:any = () => updateFeatureMode(mode);
		const onClickVariations:any = () => showVariationsPanel(mode);
		
		return (
			<Fragment key={key}>
				<div 
					className={className} 
					onClick={onClick} 
					onKeyDown={(e:any) => {
						if (e.key === 'Enter')
							onClick();
					}}
					data-tip={featureVariation?.getName?.() ?? getLocaleString(modeName.toLowerCase())}
					style={{left: left}}
					tabIndex={key + 100}
				>
					<img src={assetURL(`assets/images/${modeName.toLowerCase()}.png`)} />
				</div>
				{
					featureMode == mode && featureVariations?.length > 0 && _config.featureVariationsEnabled && 
					(
						<div 
							className={`${className2} drawing-option--variations`} 
							onClick={onClickVariations}
							onKeyDown={(e:any) => {
								if (e.key === 'Enter')
									onClickVariations();
							}}
							style={{left: left}}
							tabIndex={key + 101}
						>
							<img src={assetURL(`assets/images/slidedown.png`)} />
						</div>
					)
				}
			</Fragment>
		)
	}
	
	/* 
		Dimensions for the undo/delete buttons
	*/
	const minWidth:number = 500;
	const mob:boolean = _dimensions.width < minWidth;
	const mobCancel:string = mob ? ` drawing-option--cancel--mob` : '';
	const mobUndo:string = mob ? ` drawing-option--undo--mob` : '';
	
	const drawCancelDrawingButton = (toolsDrawn:number) => {
		if(!state.drawingPolygon) return;
		
		const left1:number = toolsLeft + (toolsDrawn * toolPadding);
		const left2:number = toolsLeft + ((toolsDrawn + 1) * toolPadding);
		
		return (
			<>
				<div 
					className={`drawing-option drawing-option--cancel${mobCancel}`} 
					onClick={clickAbortDrawing} 
					data-tip={getLocaleString('abortdrawing')}
					style={!mob ? {left: left1} : null}
				>
					<img src={assetURL(`assets/images/cancel.png`)} />
				</div>
				<div 
					className={`drawing-option drawing-option--undo${mobUndo}`} 
					onClick={clickUndoPoint} 
					data-tip={`${getLocaleString('undopoint')} (Ctrl+Z)`}
					style={!mob ? {left: left2} : null}
				>
					<img src={assetURL(`assets/images/undo.png`)} />
				</div>
			</>
		)
	}
	const drawRevertButton = (toolsDrawn:number) => {
		const left:number = toolsLeft + (toolsDrawn * toolPadding);
		
		// Clear dt-undo event handler (one off)
		_events.unall('dt-undo');
		
		if(state.drawingPolygon) return;
		if(cookieCuts.length < 1) return;
		if(featureMode != FeatureMode.LinearRing) return;
		
		const revertCookieCut = () => {
			const format:GeoJSON = drawingSource.get('feature-format');
			const cuts:CookieCut[] = [...cookieCuts];
			const cut:CookieCut = cuts.shift();
			
			cut.remnantPairs.forEach((remnantPair:RemnantPair) => {
				const resultFeature:Feature<Geometry> = cookieCutReverse(
					remnantPair.newFeature,
					remnantPair.originFeature,
					format
				);
				
				// Remove the cookie cutted feature and replace with restored one
				removeFeatureFromSource(remnantPair.cookieCuttedFeature, drawingSource);
				
				insertFeature(resultFeature, {addToSource: true, serverSync: false});
				
				// Set the cookie cutted feature reference to the new result feature (above)
				cuts.forEach((cut:CookieCut) => {
					cut.remnantPairs.forEach((remnantPair:RemnantPair) => {
						if(remnantPair.cookieCuttedFeature.get('feature-id') == resultFeature.get('feature-id'))
							remnantPair.cookieCuttedFeature = resultFeature;
					});
				});
			});
			
			setCookieCuts(cuts);
			
			// Sync all
			syncDrawTool();
		}
		
		// Assign shortcut event handler
		_events.on('dt-undo', revertCookieCut);
		
		return (
			<>
				<div 
					className={`drawing-option drawing-option--undo${mobUndo}`} 
					onClick={() => revertCookieCut()} 
					data-tip={`${getLocaleString('undo')} (Ctrl+Z)`}
					style={{left: left}}
				>
					<img src={assetURL(`assets/images/undo.png`)} />
				</div>
			</>
		)
	}
	const drawDrawTool = () => {
		const featureModeRows:any[] = drawFeatureModes();
		
		return (
			<>
				{featureModeRows}
				{drawCancelDrawingButton(featureModeRows.length)}
				{drawRevertButton(featureModeRows.length)}
			</>
		)
	}
	const clickAbortDrawing = () => {
		if(!confirm(getLocaleString('drawingcancelconf'))) return;
		
		abortDrawing();
		_ui.tooltip.hide();
	}
	const clickUndoPoint = () => {
		getActiveInteraction().removeLastPoint();
	}
	
	state.onDrawingPolygon = (s:boolean) => {
		state.drawingPolygon = s;
		setDrawingPolygon(s);
	}
	
	if(!state.initialized) initialize();
	
	updateCurrentToolMode(currentToolMode);
	updateSnapping(zoomLevel);
	
	// Rebuild tooltip when document elements change
	useEffect(() => _ui.tooltip.rebuild(), [currentToolMode, drawingPolygon]);
	
	return (
		<>
			{state.currentToolMode == MapToolMode.Draw && drawDrawTool()}
		</>
	)
}

/*
	Initializes the module
*/
function initialize() {
	const format:GeoJSON = new GeoJSON({ featureProjection: mapConfig.epsgCodeView });
	
	setupState();
	setupInteractions();
	setupMap();
	
	// Add references to the sources
	drawingSource.set('feature-format', format);
	sitesSource.set('feature-format', format);
	
	// Style the layers
	drawingLayer.setStyle(StyleFeature);
	sitesLayer.setStyle(StyleSitesFeature);
	
	// Global events
	_events.on('featuresaved', onFeatureSaved);
	_events.on('featuresaved-nosync', onFeatureSavedNoSync); // Used in popups/UploadTool
	_events.on('featureformatted', onFeatureFormatted);
	_events.on('sync-drawtool', syncDrawTool);
	_events.on('scale-line-units-changed', onScaleLineUnitsChanged);
	
	/*
		* New map started event
		* 
		* @param {OlMap} newMap - The new map that's being started
		* 
		* @remarks
		* Re-setup of map events when a new map is started during runtime. 
		* This is called before the re-render, therefore we update the
		* map var early so the setupMap function can access the new map.
		* Map related initialisation is all we call as everything else is not map dependent.
	*/
	_events.on('new-map-started', (newMap:OlMap) => {
		if(newMap == map) return;
		map = newMap;
		setupMap();
	});
	
	/*
		* Mouse moved event to update the sketch features (temporary features) 
		* in the active interaction.
		* 
		* @param {Vector2} mouse position
	*/
	_events.on('mouse-move', (mousePos:Vector2) => {
		if(!isDrawToolSelected()) return;
		
		const interaction:Draw = getActiveInteraction();
		
		if(!interaction) return;
		
		const featureVariation:FeatureVariation = state.activeFeatureVariations.get(FeatureMode[state.featureMode]);
		
		// Update the 'sketch' feature(s) attribute to active variation. Allow undefined.
		const sketchFeatures:Feature<Geometry>[] = interaction.getOverlay().getSource().getFeatures();
		
		sketchFeatures.forEach((feature:Feature<Geometry>) => {
			feature.set('sketch', sketchFeatures.indexOf(feature) == 0);
			feature.set('variation-id', featureVariation?.id);
			feature.set('interaction-name', getActiveInteractionName());
		});
	});
	
	/*
		Remove feature handler for drawing tool
	*/
	_events.on('remove-feature', (feature:Feature<Geometry>) => {
		if(!isFeatureMine(feature) && !_config.siteEditor)
			return;
		
		deleteFeature(feature, drawingSource);
		snapFeatures.remove(feature);
		
		isFeatureMine(feature) && syncDrawTool();
	});
	
	/*
		Add WKT feature event from parent window handler
	*/
	_events.on('window:add-editable-feature', (event:MessageEvent) => {
		const wktString:string = event.data.payload.wkt;
		const focusOn:boolean = event.data.payload.focusOn;
		const acId:string = event.data.payload.acId;
		
		const feature:Feature<Geometry> = parseWKTFeature(wktString, mapConfig);
		const geometry:Geometry = feature.getGeometry();
		const extent:number[] = geometry.getExtent();
		const center:any = getCenter(extent);
		
		feature.set('origin', 'AC');
		feature.set('ac-id', acId);
		
		insertFeature(feature, {serverSync: false});
		
		if(focusOn)
			focusFeatures(map, [feature]);
	});
	
	/*
		Snapping event
	*/
	vectorTileLayer.getSource().on('tileloadend', (e:any) => {
		const tile:VectorTile = e.tile as VectorTile;
		const features:any[] = tile.getFeatures();
		
		snapFeatures.extend(features);
	});
	
	/*
		Only load tiles within the correct zoom level range
	*/
	const vtlLoadFunction:Function = vectorTileLayer.getSource().getTileLoadFunction();
	
	vectorTileLayer.getSource().setTileLoadFunction((tile:VectorTile, url:string) => {
		const zoom:number = map.getView().getZoom();
		let zoomLoad:number = Math.round(zoom);
		
		if(zoomLoad < _config.minZoomSnapping) {
			tile.setState(TileState.ERROR);
			return;
		}
		
		/*
			If the zoom is above maxZoomSnapping, then just re-use the max zoom level pbf for these tiles
		*/
		if(zoomLoad > maxZoomSnapping) {
			zoomLoad = maxZoomSnapping;
		}
		
		/*
			Insert the modified zoom level into the url
		*/
		const urlParts:Array<string> = url.split('/');
		urlParts[urlParts.length - 3] = zoomLoad.toString();
		url = urlParts.join('/');
		
		vtlLoadFunction(tile, url);
	});
	
	// Capture keyboard input
	window.addEventListener('keydown', handleKeyDown);
	window.addEventListener('keyup', handleKeyUpCallback);
	
	state.initialized = true;
}

/*
	State setup
*/
function setupState() {
	if(mapConfig.featureVariations?.['Polygon']?.length > 0) {
		state.activeFeatureVariations.set(
			'Polygon', 
			new FeatureVariation(mapConfig.featureVariations['Polygon'][
				findDefaultIndex(mapConfig.featureVariations['Polygon'])
			])
		);
	}
	if(mapConfig.featureVariations?.['Point']?.length > 0) {
		state.activeFeatureVariations.set(
			'Point', 
			new FeatureVariation(mapConfig.featureVariations['Point'][
				findDefaultIndex(mapConfig.featureVariations['Point'])
			])
		);
	}
	if(mapConfig.featureVariations?.['LineString']?.length > 0) {
		state.activeFeatureVariations.set(
			'LineString', 
			new FeatureVariation(mapConfig.featureVariations['LineString'][
				findDefaultIndex(mapConfig.featureVariations['LineString'])
			])
		);
	}
}

/*
	Map setup
*/
function setupMap() {
	// Add drawing layer
	map.addLayer(vectorTileLayer);
	map.addLayer(sitesLayer);
	map.addLayer(drawingLayer);
	map.addLayer(linearRingLayer);
	
	// Add interactions
	map.addInteraction(Interactions.Polygon);
	map.addInteraction(Interactions.Point);
	map.addInteraction(Interactions.LineString);
	map.addInteraction(Interactions.LinearRing);
	map.addInteraction(Interactions.Snap); // Snap goes last
	
	// Set an easy to access drawing layer reference for other modules
	map.set('drawing-layer', drawingLayer);
	map.set('sites-layer', sitesLayer);
	map.set('snap-features', snapFeatures);
	
	// Hide tooltip when map manipulated (mainly for mobile devices)
	map.getView().on('change:center', () => _ui.tooltip.hide());
	map.getView().on('change:rotation', () => _ui.tooltip.hide());
	map.getView().on('change:resolution', () => _ui.tooltip.hide());
	
	// Map click static feature handler
	map.on('click', (e:any) => {
		if(_ui.isPointerBusy()) return;
		
		map.forEachFeatureAtPixel(e.pixel, (feature:Feature<Geometry>) => {
			if(feature.get('highlight')) {
				/*_ui.showConfirm(getLocaleString('removehl'), () => {
					sitesSource.removeFeature(feature);
				});*/
				sitesSource.removeFeature(feature);
				return;
			}
			
			const site:ExistingSite = feature.get('existing-site');
			
			if(!site) return;
			
			const callback:any = window[_config.existingSitesCallback as any];
			
			callback?.(site.formId);
		});
	});
	
	// Load existing features from local storage
	loadFeatures();
}

/*
	Interactions setup
*/
function setupInteractions() {
	Interactions.Polygon = new Draw({
		source: drawingSource,
		type: 'Polygon',
		snapTolerance: 8 * getSnapToleranceMultiplier(),
		style: StyleFeature
	});
	Interactions.Point = new Draw({
		source: drawingSource,
		type: 'Point',
		snapTolerance: 8 * getSnapToleranceMultiplier(),
		style: StyleFeature
	});
	Interactions.LineString = new Draw({
		source: drawingSource,
		type: 'LineString',
		snapTolerance: 8 * getSnapToleranceMultiplier(),
		style: StyleFeature
	});
	Interactions.LinearRing = new Draw({
		source: linearRingSource,
		type: 'Polygon',
		snapTolerance: 8 * getSnapToleranceMultiplier(),
		style: StyleFeatureLinearRing
	});
	Interactions.Snap = new Snap({
		features: snapFeatures,
		pixelTolerance: 8 * getSnapToleranceMultiplier()
	});
	
	const startInteractionRoutine = (interaction:Draw, feature:Feature<Geometry>, noInterval:boolean = false) => {
		const featureVariationUpdate = (feature:Feature<Geometry>) => {
			const featureVariation:FeatureVariation = state.activeFeatureVariations.get(FeatureMode[state.featureMode]);
			
			if(featureVariation) {
				feature.set('variation-id', featureVariation.id);
			}
		}
		const featureChanged = (feature:Feature<Geometry>) => {
			formatFeature(feature, onFeatureFormatted);
			featureVariationUpdate(feature);
		}
		
		featureVariationUpdate(feature);
		
		if(!noInterval) {
			clearInterval(interaction.get('draw-interval'));
			
			interaction.set('draw-interval', setInterval(featureChanged, 100, feature));
		}
	}
	const endInteractionRoutine = (interaction:Draw) => {
		clearInterval(interaction.get('draw-interval'));
	}
	const cleanProperties = (feature:Feature<Geometry>) => {
		feature.unset('sketch');
		//feature.unset('interaction-name');
		feature.unset('inprogress');
		
		feature.set('interaction-name', getActiveInteractionName());
		feature.set('origin', 'drawn'); // This feature was drawn
		
		if(_config.siteEditor)
			feature.set('origin', 'AC'); // For AC use only
	}
	
	// Events for the interactions
	
	/*
		Start
	*/
	Interactions.Polygon.on('drawstart', (e:any) => {
		state.onDrawingPolygon(true);
		
		const feature:Feature<Geometry> = e.feature;
			
		feature.set('inprogress', true);
		
		startInteractionRoutine(Interactions.Polygon, feature);
	});
	Interactions.LineString.on('drawstart', (e:any) => {
		state.onDrawingPolygon(true); // use the same thing
		
		const feature:Feature<Geometry> = e.feature;
			
		feature.set('inprogress', true);
		
		startInteractionRoutine(Interactions.LineString, feature);
	});
	Interactions.LinearRing.on('drawstart', (e:any) => {
		state.onDrawingPolygon(true); // use the same thing
		
		const feature:Feature<Geometry> = e.feature;
			
		feature.set('inprogress', true);
		
		startInteractionRoutine(Interactions.LinearRing, feature);
	});
	
	/*
		End
	*/
	Interactions.Polygon.on('drawend', (e:any) => {
		e.preventDefault();
		
		const feature:Feature<Geometry> = e.feature;
		
		endInteractionRoutine(Interactions.Polygon);
		
		cleanProperties(feature);
		insertFeature(feature, {selfIntersectionChecking: true, isNew: true});
	});
	Interactions.Point.on('drawend', (e:any) => {
		e.preventDefault();
		
		const feature:Feature<Geometry> = e.feature;
		
		startInteractionRoutine(Interactions.Point, feature, true);
		
		cleanProperties(feature);
		insertFeature(feature, {isNew: true});
	});
	Interactions.LineString.on('drawend', (e:any) => {
		e.preventDefault();
		
		const feature:Feature<Geometry> = e.feature;
		
		endInteractionRoutine(Interactions.LineString);
		
		cleanProperties(feature);
		insertFeature(feature, {isNew: true});
	});
	Interactions.LinearRing.on('drawend', (e:any) => {
		e.preventDefault();
		
		const feature:Feature<Geometry> = e.feature;
			
		state.onDrawingPolygon(false);
		
		endInteractionRoutine(Interactions.LinearRing);
		
		const result:CookieCut = cookieCut(feature, drawingSource);
		
		handleCookieCutResult(result);
			
		// Don't need to keep these cookie cut features, clear each time
		linearRingSource.clear(true);
	});
}

/*
	Handle the result of a cookie cut operation
*/
function handleCookieCutResult(result:CookieCut) {
	if(result.newFeatures.length > 0) {
		
		// Delete old features without sync
		result.oldFeatures.forEach((feature:Feature<Geometry>) => {
			removeFeatureFromSource(feature, drawingSource);
		});
		
		// Add these new features without syncing each time
		result.newFeatures.forEach((feature:Feature<Geometry>) => {
			insertFeature(feature, {addToSource: true, serverSync: false});
		});
		
		// Add the cookie cut operation to the array for reverting
		_events.invoke('on-cookie-cut', result);
		
		// Sync all
		syncDrawTool();
	}
}

/*
	Toggle interactions based on drawing mode and the active map tool
*/
function updateInteractionToggle() {
	const featureMode:FeatureMode = state.featureMode;
	const toolMode:MapToolMode = state.currentToolMode;
	
	Interactions.Polygon.setActive(
		featureMode == FeatureMode.Polygon &&
		toolMode == MapToolMode.Draw
	);
	Interactions.Point.setActive(
		featureMode == FeatureMode.Point &&
		toolMode == MapToolMode.Draw
	);
	Interactions.LineString.setActive(
		featureMode == FeatureMode.LineString &&
		toolMode == MapToolMode.Draw
	);
	Interactions.LinearRing.setActive(
		featureMode == FeatureMode.LinearRing &&
		toolMode == MapToolMode.Draw
	);
}

/*
	Toggle snapping based on zoom level
*/
function updateSnapping(zoomLevel:number) {
	const enable:boolean = zoomLevel >= _config.minZoomSnapping && state.currentToolMode != MapToolMode.Annotation;
	
	Interactions.Snap.setActive(enable);
}

/**
	* Insert a *new* feature to drawing source
	*
	* @param {Feature<Geometry>} feature		-	Feature to add
	* @param {InsertOptions} options            -   Options for insert
	* 
	* @remarks
	* This method contains the safety checking for new features
	* such as intersections and area limits and does not add the
	* feature to the source if it is not suitable.
	*
	* When calling this method after a feature was drawn by the user,
	* `addToSource` is passed as false because ol does this automatically.
*/
function insertFeature(feature:Feature<Geometry>, options:InsertOptions = {}) {
	const serverSync:boolean = options.serverSync ?? true;
	const addToSource:boolean = options.addToSource ?? true;
	const selfIntersectionChecking:boolean = options.selfIntersectionChecking ?? false;
	
	const featureType:string = feature.getGeometry().getType();
	
	var featureConflict:Feature<Geometry> = null;
	
	feature.setId(getNextFeatureId(drawingSource));
	
	formatFeature(feature, onFeatureFormatted, options.isNew ? ChangelogMode.Created : ChangelogMode.None);
	
	// The save method for this function only
	const onSave = () => (serverSync ? onFeatureSaved : onFeatureSavedNoSync)(feature);
	
	switch(featureType) {
		case 'Polygon':
			state.onDrawingPolygon(false);
			
			// polygonIntersects() handles polygon shape intersections only
			// This will check for only polygon to polygon intersections
			if(_config.intersectionChecking)
				featureConflict = polygonIntersects(feature, drawingSource) ?? (_config.existingSitesIntersectionChecking ? polygonIntersects(feature, sitesSource) : null);
			
			if(selfIntersectionChecking) {
				if(getIsSelfIntersecting(feature)) {
					removeFeatureFromSource(feature, drawingSource);
					_ui.showError(getLocaleString('kinkederror'));
					return;
				}
			}
		break;
		case 'Point':
			// featureInsersects() handles points & polygons but doesn't differentiate borders so it is only useful for points
			// This will check for only point to point intersections
			if(_config.intersectionChecking)
				featureConflict = featureInsersects(feature, drawingSource, featureType) ?? (_config.existingSitesIntersectionChecking ? featureInsersects(feature, sitesSource) : null);
		break;
		case 'LineString':
			state.onDrawingPolygon(false);
		break;
		case 'LinearRing':
			// Discontinue
			return;
	}
	
	if(feature.get('Notes') == null)
		feature.set('Notes', '');
	
	if(featureConflict) {
		const typeFeatureDrawing:string = featureType.toLowerCase();
		let typeFeatureIntersects:string = featureConflict.getGeometry().getType().toLowerCase(); // technical type name of feature
		
		// change technical type name of feature to something more understandable
		typeFeatureIntersects = !featureConflict.get('existing-site') ? 
		(typeFeatureIntersects == 'point' ? getLocaleString('anotherpoint') : getLocaleString('anotherdrawing')) 
		: getLocaleString('anexistingsite');
		
		_ui.showError(getLocaleString('intersects', [typeFeatureDrawing, typeFeatureIntersects]), getLocaleString('drawingerror'));
		removeFeatureFromSource(feature, drawingSource);
		return;
	}
	
	if(feature.get('area-m2') < _config.minimumPolygonArea && featureType == 'Polygon' && serverSync) {
		_ui.showError(getLocaleString('polytoosmall'), getLocaleString('drawingerror'));
		removeFeatureFromSource(feature, drawingSource);
		return;
	}
	if(feature.get('area-m2') > _config.maximumPolygonArea && featureType == 'Polygon' && serverSync) {
		_ui.showError(getLocaleString('polytoobig'), getLocaleString('drawingerror'));
		removeFeatureFromSource(feature, drawingSource);
		return;
	}
	
	if(options.isNew) {
		const { success: isValidFeature, error: validationError } = validateFeature(feature);
		if(!isValidFeature) {
			_ui.showError(
				getLocaleString('invalidfeature', [getLocaleString(validationError)]), 
				getLocaleString('drawingerror')
			);
			removeFeatureFromSource(feature, drawingSource);
			return;
		}
		if(_config.siteEditor) {
			const existingFeature:Feature<Geometry> = drawingSource.getFeatures().find(
				f => f.get('origin') === 'AC' && !f.get('collapsed')
			);
			if(existingFeature) {
				const existingType:string = existingFeature.getGeometry().getType();
				if((existingType == 'Polygon' || existingType == 'MultiPolygon') && featureType == 'Polygon') {
					// Concatinate this polygon feature to the existing polygon/multipolygon
					addPolygonToFeature(
						existingFeature as Feature<MultiPolygon | Polygon>,
						feature as Feature<Polygon>
					);
					removeFeatureFromSource(feature, drawingSource);
					return;
				}else{
					_ui.showError(getLocaleString('siteeditor-already-drawn'), getLocaleString('notice'));
					removeFeatureFromSource(existingFeature, drawingSource); // remove existing one off the map
				}
			}
		}
	}
	
	if(addToSource)
		drawingSource.addFeature(feature);
		
	saveFeaturesLocal(drawingSource);
	onSave();
	
	/* Callback event */
	onFeatureDrawEvent(DrawEvent.New, featureType);
	
	////////////////////
	// Site meta data
	////////////////////
	const siteInfo = buildSiteInfo(feature, {
		source: drawingSource,
		sitesSource: sitesSource
	});
	
	/* Parent window callback event */
	_events.invoke('parent-invoke', {
		event: 'site-drawn', 
		...siteInfo
	});
}

/*
	Load features
*/
function loadFeatures() {
	//loadLocalFeatures();
	loadRemoteFeatures();
	loadExistingSites();
}

/*
	Load features from local storage
*/
function loadLocalFeatures() {
	const jsonData:string = localStorage.getItem(_config.getLocalStorageId());
	
	if(!jsonData) {
		console.log('No saved feature data: ' + jsonData);
		return;
	}
	
	let features:Array<GeoJSONFeature> = [];
	
	try {
		features = JSON.parse(jsonData);
	} catch(e:any) {
		console.log('Corrupt saved feature data. Removing it.')
		localStorage.removeItem(_config.getLocalStorageId());
		return;
	}
	
	addFeatures(features);
}

/*
	Load existing sites features from remote source
*/
function loadExistingSites() {
	const format:GeoJSON = new GeoJSON({ featureProjection: mapConfig.epsgCodeView });
	
	const url:string = _config.existingSitesSource;
	
	if(!url) return;
	
	const batchSize:number = 10;
	const pauseLength:number = 100; // in ms
	
	const promises:Function[] = [];
	let sitesCount:number = 0;
	
	const loadInBatch = () => {
		const batch:Promise<any>[] = [];
		
		while(batch.length < batchSize && promises.length > 0)
			batch.push(promises.shift()());
		
		if(batch.length < 1 && promises.length < 1)
			return;
			
		Promise.all(batch).then((values:any[]) => {
			console.log(`Sites load progress: ${Math.floor((sitesCount-promises.length) / sitesCount * 100)}%`);
			setTimeout(() => loadInBatch(), pauseLength);
		});
	}
	
	fetchJson(url)
	.then((sites:any) => {
		if(!sites) return;
		
		Object.entries(sites).forEach(([key, value]) => {
			const siteObj:any = sites[key];
			const storageId:number = _config.getStorageId();
			const site:ExistingSite = new ExistingSite({id: key, formId: siteObj.formid});
			const url:string = `https://${mapConfig.client}.opus4.co.uk/api/v1/call-for-sites/${storageId}/${site.id}/`;
			
			const promise = () => {
				return new Promise((accept, reject) => {
					fetchJson(url)
					.then((geoJsonFeatureCollection:any) => {
						let ids:number = 0;
						let features:Feature<Geometry>[] = format.readFeatures(geoJsonFeatureCollection);
						
						features.forEach((feature:Feature<Geometry>) => {
							// Assign unique id to existing sites feature
							feature.setId(`ES${key}-${ids}`);
							
							feature.set('existing-site', site);
							
							// Add existing site geometry to snap?
							addFeatureToSnap(feature);
							
							sitesSource.addFeature(feature);
							
							ids++;
						});
						
						accept(true);
					})
					.catch((error:any) => {
						console.log('There was a problem parsing one of the existing sites features at url ' + url);
						console.log(error);
						accept(false);
					});
				});
			};
			
			promises.push(promise);
		});
		
		sitesCount = promises.length;
		loadInBatch();
	});
}

/*
	Load remote features from database
*/
function loadRemoteFeatures() {
	const storageId:number = _config.getStorageId();
	const url:string = `https://${mapConfig.client}.opus4.co.uk/api/v1/call-for-sites/${storageId}/${_config.UUID}/`;
	const options:any = {
		method: 'GET',
		headers: new Headers(),
		mode: 'cors',
		cache: 'default'
	}
	
	fetchRequestJson(url, options)
	.then((features:any) => {
		const featuresAdd:Array<GeoJSONFeature> = features.features.filter(
			(feature:GeoJSONFeature) => {
				const geometry:any = feature.geometry;
				const coordinates:any = geometry?.coordinates ?? [1];
				
				return coordinates.length > 0;
			}
		);
		
		addFeatures(featuresAdd);
		
		// Focus on all features if config says so
		_events.invoke('remote-features-loaded', features);
	})
	.catch((error) => {
		console.log(`Error parsing remote JSON features: ${error}`);
	});
}

/*
	Add existing features to drawing source & snapping interaction via GeoJSONFeature array
*/
function addFeatures(features:Array<GeoJSONFeature>) {
	const format:GeoJSON = new GeoJSON({ featureProjection: mapConfig.epsgCodeView });
	
	for(let geoJsonFeature of features) {
		let feature:Feature<Geometry> = format.readFeature(geoJsonFeature);
		
		insertFeature(feature, {addToSource: true, serverSync: false});
	}
}

/*
	Updates current tool mode. Called each render.
	(Tool mode is the tool that is selected from the toolbar)
*/
function updateCurrentToolMode(currentToolMode:MapToolMode) {
	state.currentToolMode = currentToolMode;
	updateInteractionToggle();
}

/*
	Abort drawing mid-draw
*/
function abortDrawing() {
	const featureMode:FeatureMode = state.featureMode;
	
	switch(featureMode) {
		case FeatureMode.Polygon:
			state.onDrawingPolygon(false);
			Interactions.Polygon.abortDrawing();
		break;
		case FeatureMode.LineString:
			state.onDrawingPolygon(false);
			Interactions.LineString.abortDrawing();
		break;
		case FeatureMode.LinearRing:
			state.onDrawingPolygon(false);
			Interactions.LinearRing.abortDrawing();
		break;
	}
}

/*
	Undo/redo feature - in progress
*/
function undoFeature() {
	
}
function redoFeature() {
	
}

function isDrawToolSelected():boolean {
	return state.currentToolMode == MapToolMode.Draw;
}

/*
	Update snap
*/
function updateSnap() {
	const shouldSnap:boolean = !_ui.isSpaceDown();
	
	Interactions.Snap.setActive(shouldSnap);
}

function handleKeyDown(e:KeyboardEvent) {
	// Something like an input box is selected so abort this handler
	if(_ui.hasFocus()) return;
	
	updateSnap();
	
	if(e.key == 'Escape' && isDrawToolSelected()) {
		e.preventDefault();
		abortDrawing();
	}
	if(e.key == 'z' && _ui.isCtrlDown() && !_ui.isShiftDown()) {
		e.preventDefault();
		if(isDrawToolSelected() && state.drawingPolygon) {
			switch(state.featureMode) {
				case FeatureMode.Polygon:
					Interactions.Polygon.removeLastPoint();
				break;
				case FeatureMode.LineString:
					Interactions.LineString.removeLastPoint();
				break;
				case FeatureMode.LinearRing:
					Interactions.LinearRing.removeLastPoint();
				break;
			}
		}else{
			undoFeature();
		}
		_events.invoke('dt-undo');
	}
	if(e.key == 'z' && _ui.isCtrlDown() && _ui.isShiftDown()) {
		e.preventDefault();
		redoFeature();
	}
}
	/*
	Handle key up
*/
const handleKeyUpCallback = (e:KeyboardEvent) => {
	// Something like an input box is selected so abort this handler
	if(_ui.hasFocus()) return;
	
	updateSnap();
}

/* 
	Called after feature is saved/deleted
	Adds/removes feature from snapping interaction
*/
function onFeatureSaved(feature:Feature<Geometry>) {
	if(!isFeatureMine(feature))
		return;
		
	addFeatureToSnap(feature);
	syncDrawTool();
}
function onFeatureSavedNoSync(feature:Feature<Geometry>) {
	if(!isFeatureMine(feature))
		return;
		
	addFeatureToSnap(feature);
}
function onFeatureFormatted(feature:Feature<Geometry>) {
	feature.set('get-zoom', ():number => map.getView().getZoom()); // So styled feature can get the zoom level
}
function isFeatureMine(feature:Feature<Geometry>) {
	switch(feature.get('origin')) {
		case 'drawn':
		case 'uploaded':
			return true;
		case undefined: {
			// Fallback for when no origin is set
			feature.set('origin', 'drawn');
			return true;
		}
		default:
			return false;
	}
}

/*
	* Re-format all features after scale line unit changed
	* 
	* @param {Units} units - Units that the scale line now uses
	* 
	* @remarks
	* The re-format is mainly to update the size-info attribute
	* of each feature
*/
function onScaleLineUnitsChanged(units:Units) {
	const allFeatures:Feature<Geometry>[] = getFeatures(drawingSource);
		
	allFeatures.forEach((feature:Feature<Geometry>) => {
		formatFeature(feature, onFeatureFormatted);
	});
}

/**
	Send a POST request with saved features after 0.5s
*/
function syncDrawTool() {
	// Save features to remote server
	
	clearTimeout(saveTimer);
	saveTimer = setTimeout(() => saveFeaturesRemote(map, drawingSource), 500);
}

/**
	* Adds feature to snapping interaction
	* 
	* @param feature	-	Feature to add
	* 
	* @remarks
	* Try/catch is added because collection throws 
	* exception if feature is not unique.
*/
function addFeatureToSnap(feature:Feature<Geometry>) {
	try {
		snapFeatures.push(feature);
	}catch(e:any) {};
}

/*
	* Get active interaction
*/
function getActiveInteraction():Draw {
	if(Interactions.Polygon.getActive())
		return Interactions.Polygon;
	
	if(Interactions.Point.getActive())
		return Interactions.Point;
		
	if(Interactions.LineString.getActive())
		return Interactions.LineString;
		
	if(Interactions.LinearRing.getActive())
		return Interactions.LinearRing;
}

/*
	* Get active interaction name
	* 
	* @param {string} Interaction name
*/
function getActiveInteractionName():string {
	if(Interactions.Polygon.getActive())
		return 'Polygon';
	
	if(Interactions.Point.getActive())
		return 'Point';
		
	if(Interactions.LineString.getActive())
		return 'LineString';
		
	if(Interactions.LinearRing.getActive())
		return 'LinearRing';
}